import type { ReactNode } from "react"

import type { EstadoPaso, PasoTraspaso } from "@/routes/onboarding/api"
import { t } from "@/lib/i18n"

/**
 * EL TEXTO DE CADA PASO, EN LA VOZ DE MARTY.
 *
 * Vive en la SPA y no en el backend a proposito. El backend sabe HECHOS
 * —cuantas sociedades no tienen identificador, cuantas lineas no estan
 * mapeadas— y esta pantalla sabe decirselos a alguien que acaba de llegar de
 * otra plataforma y no conoce el vocabulario de esta.
 *
 * ---------------------------------------------------------------------------
 * QUIEN HABLA, Y DE QUIEN ES EL TRABAJO
 *
 * Brandbook, lamina 25: **primera persona**. «Marty habla, no el sistema». Eso
 * no es un retoque de redaccion: cambia de quien es el trabajo. La version
 * anterior decia «sube las cosas a la plataforma» y «el sistema lee el
 * contenido de cada archivo» — dos frases que ponen al cliente a operar una
 * maquina. Marty dice «dame la carpeta y yo la leo».
 *
 * La regla de redaccion de todo lo de abajo, en ese orden:
 *
 *   1. YO HAGO, TU DAS.     Cada paso pide UN dato y promete lo que Marty hace
 *                           con el. Nunca una instruccion de operacion.
 *   2. EL CLIENTE NO PREPARA NADA. Cada «exportalo en este formato» es friccion
 *                           y una fuente de error nueva.
 *   3. UNA IDEA POR FRASE.  Cuanto mas grave, mas corta.
 *
 * ---------------------------------------------------------------------------
 * EL TEXTO LARGO NO SE BORRO: SE PLEGO
 *
 * Cesar pidio menos texto, y tenia razon — la pantalla eran trece fichas con
 * cinco parrafos cada una. Pero lo que habia escrito en `porque` no es relleno:
 * casi todo se aprendio cargando cuatro paises de verdad y equivocandose (la
 * factura del contador archivada entre las ventas, el balance que sale vacio y
 * cuadrando, los comprobantes que se detienen antes del libro). Borrarlo seria
 * perder lo unico que no se puede volver a deducir mirando el codigo.
 *
 * Por eso `porque` sigue entero y **la pantalla lo pliega**: se lee de corrido
 * en cuatro lineas y el que quiera el fundamento lo abre. Lo que si se acorto
 * de verdad es `que` y `como`, que eran parrafos donde cabia una linea.
 */

/**
 * El formulario que se rellena DENTRO del paso, sin salir de la pantalla.
 *
 * ERAN CINCO Y SON DIEZ, y el cambio viene de los puntos 14 a 20 de Cesar, que
 * son un solo encargo: **el traspaso no te puede echar fuera.** El criterio
 * anterior —«formulario cuando falta UN DATO, enlace cuando falta un JUICIO»— es
 * bueno y se mantiene; lo que estaba mal era la aplicación. Cargar bancos,
 * cotizaciones, personas y facturación no es un juicio: es un archivo o unos
 * campos, y los cuatro mandaban a otra pantalla.
 *
 * Los que SIGUEN llevando a su pantalla son los que de verdad son un juicio:
 * revisar la cola de comprobantes y revisar el mapeo de cuentas. Ahí el enlace
 * no es pereza — meter una cola de 300 filas dentro de una ficha la haría
 * inservible en los dos sitios—. Pero ahora van acompañados del dato que sí se
 * puede dar aquí, así que el paso avanza sin salir.
 */
export type Formulario =
  | "sociedad"
  /** Los tres a la vez: qué meses, de dónde (Drive) y los archivos. */
  | "comprobantes"
  | "equipo"
  | "maestros"
  | "bancos_fx"
  | "facturacion"
  | "historico"

export interface TextoPaso {
  titulo: string
  /**
   * Lo que Marty dice de este paso. Primera persona, una frase, sin punto
   * final de mas. Es lo unico que se lee siempre.
   */
  marty: string
  /** Que hace falta de ti. Una linea. */
  que: ReactNode
  /** De donde sale, cuando no es obvio. Se pliega con el resto. */
  como?: ReactNode
  /**
   * Por que este paso va donde va. Se pliega: es el fundamento, no la
   * instruccion. Casi todos estos ordenes se aprendieron con datos reales.
   */
  porque?: ReactNode
  /**
   * El formulario que se abre DENTRO del paso.
   *
   * Es la correccion de fondo de esta pantalla: un paso que sabe que falta algo
   * y te manda a otra pantalla a arreglarlo es media pantalla, y en el modo
   * guiado —una pregunta a la vez— te saca del cuestionario en la segunda
   * pregunta y ya no vuelves.
   */
  formulario?: Formulario
  /**
   * A donde ir, cuando el trabajo de verdad vive en otra pantalla.
   *
   * Solo donde NO hay formulario, y son los pasos cuya accion es revisar algo
   * —una cola de comprobantes, un mapeo de cuentas, un asiento— que no cabe en
   * un campo de texto. Ahi el enlace no es pereza: es que el trabajo es otro.
   */
  accion?: { etiqueta: string; a: string }
  /** Cuando el paso junta dos preguntas con dos pantallas: una acción por cada una. */
  acciones?: { etiqueta: string; a: string }[]
  /**
   * Que pasa si esto se deja para despues. Se muestra al posponer: posponer los
   * saldos de apertura y posponer la carga de personas tienen consecuencias muy
   * distintas y las dos se llaman igual en el boton.
   */
  siSePospone?: string
}

export const TEXTOS: Record<string, TextoPaso> = {
  sociedad: {
    titulo: "Tu sociedad",
    marty:
      "Necesito saber quién eres frente al fisco. Con eso distingo una factura que emitiste tú de una que te emitieron.",
    que: "El identificador fiscal —RUC, RFC, CNPJ o RUC panameño— y el régimen tributario.",
    como: "Del documento oficial: ficha RUC, constancia de situación fiscal, cartão CNPJ o aviso de operación. No de memoria y no de una factura vieja.",
    formulario: "sociedad",
    siSePospone:
      "Nada más puede avanzar: sin sociedad no hay libros, ni períodos, ni comprobantes.",
    porque: (
      <>
        {t("Va primero porque sin identificador no puedo decidir si un comprobante lo emitiste tú o te lo emitieron. Suena obvio y no lo es: en un traspaso real apareció la factura del contador archivada dentro de la carpeta de ventas. Ese dato es la diferencia entre registrar un gasto e inventar un ingreso.")}</>
    ),
  },

  plan: {
    titulo: "Tu plan de cuentas",
    marty: "Dime con qué cuentas quieres leer tu resultado, y traduzco las tuyas al plan común. Si esto falla, los estados salen vacíos y cuadrando.",
    que: "Dos cosas: qué es ingreso, costo de ventas y gasto en tu negocio (el P&L), y a qué cuenta del grupo corresponde cada cuenta tuya (el mapeo).",
    como: "El P&L viene armado y se revisa; no hay que construirlo. El mapeo arranca del plan de tu país: tu contador lo revisa una vez, no cuenta por cuenta cada mes.",
    /*
      SIN FORMULARIO, Y A PROPÓSITO. Lo que falta aquí no es un dato: es DECIDIR
      —qué suma al EBITDA, a qué cuenta del grupo va cada cuenta tuya— y eso no
      cabe en una ficha. Dos pantallas, dos enlaces.
    */
    acciones: [
      { etiqueta: "Revisar el P&L", a: "/contabilidad/maestros/plan-pl" },
      { etiqueta: "Ver el mapeo", a: "/contabilidad/libros/balance" },
    ],
    siSePospone:
      "Leo tu resultado con el plan de la semilla, que contesta preguntas genéricas. Y sin mapeo los estados contables salen vacíos y cuadrando.",
    porque: (
      <>
        <strong>{t("Es el paso invisible y el más fácil de saltarse.")}</strong> {" "}{t("Sin el mapeo los asientos entran igual y los estados contables salen")}{" "}<strong>{t("vacíos y cuadrando")}</strong>{t(": un balance perfecto donde no falta el cuadre, falta el contenido. Y el P&L no lo exige ningún fisco: se diseña para contestar las preguntas de tu negocio, y ésas cambian con el negocio.")}</>
    ),
  },

  comprobantes: {
    titulo: "Tus comprobantes",
    marty:
      "Dime desde qué mes traes, dónde guardas tus documentos, y mándamelos tal como los tienes. Yo leo cada archivo por dentro; tú no ordenes nada ni renombres nada.",
    que: (
      <>
        {t("Desde y hasta qué mes vas a traer; el enlace de la carpeta de Drive donde ya los guardas, tal como está; y tus XML, PDF o un ZIP entero.")}{" "}<strong>{t("Con los archivos que no son comprobantes dentro")}</strong>
        {t(", si los hay: ya los aparto yo.")}</>
    ),
    formulario: "comprobantes",
    siSePospone: "El libro se queda vacío. Todo lo demás se puede configurar mientras tanto.",
    porque: (
      <>
        <strong>{t("Los meses son el paso invisible:")}</strong>{" "}{t("un comprobante cuyo mes no está abierto se clasifica, se trata, y se detiene justo antes del libro. Lo que se ve son dos síntomas sin relación aparente —el balance en cero y la cola llena—.")}{" "}
        {t("Declarar la carpeta no es darme acceso: pruebo el acceso antes de dar el paso por hecho. Y si tienes los")}{" "}<strong>{t("XML")}</strong>{" "}{t("—no sólo el PDF impreso— mándamelos: un PDF lo leo, un XML firmado además lo verifico.")}</>
    ),
  },

  maestros: {
    titulo: "Tu gente y tus contrapartes",
    marty: "Con tu planilla cargada te calculo el costo laboral y no se me escapa ninguna obligación.",
    que: "Las personas de tu planilla y de recibo por honorarios, y tus clientes y proveedores habituales.",
    como: (
      <>
        {t("Una por una aquí mismo, o con la plantilla que te doy rellenada con un ejemplo. Acepta lo que Excel produce de verdad: punto y coma, tildes y «1.234,56». No hay que convertir nada.")}</>
    ),
    formulario: "maestros",
    siSePospone:
      "Sin personas no hay costo laboral, y en Perú PLAME y AFPnet desaparecen del mapa de obligaciones.",
    porque: (
      <>
        {t("Cargar cuarenta personas a mano es exactamente lo que hace que nadie las cargue. Y con cero personas,")}{" "}<strong>{t("PLAME y AFPnet desaparecen del mapa de obligaciones")}</strong> {" "}{t("sin que eso sea la decisión de nadie — están entre las más sancionadas.")}</>
    ),
  },

  bancos_fx: {
    titulo: "Bancos y tipos de cambio",
    marty: "Con esto te digo cuánta caja hay y en cuánto tiempo se acaba.",
    que: "Tus cuentas bancarias con su saldo, y las cotizaciones de las monedas que uses.",
    como: "A mano o por archivo, aquí mismo. Las cotizaciones se pueden pegar directamente desde una hoja de cálculo.",
    formulario: "bancos_fx",
    siSePospone:
      "Sin tipos de cambio no puedo calcular el consolidado en absoluto. Sin bancos no hay tesorería ni runway.",
    porque: (
      <>
        {t("El runway por sociedad es el número que decide si una empresa puede pagar su planilla.")}{" "}
        <strong>{t("Y sin tipos de cambio el consolidado no se puede calcular")}</strong>{t(": los umbrales en moneda extranjera se van a revisión uno por uno.")}</>
    ),
  },

  equipo: {
    titulo: "Tu equipo",
    marty: "Necesito que haya quien prepare y quien firme. No dejo que sea la misma persona.",
    que: "Los correos de quienes van a usar esto, y con qué alcance entra cada uno.",
    formulario: "equipo",
    siSePospone:
      "Con un solo usuario el período no se puede firmar NUNCA: el cierre exige que quien firma no sea quien preparó.",
    porque: (
      <>
        <strong>{t("El cierre exige separación de funciones:")}</strong> {" "}{t("rechazo que quien preparó un período lo firme. Con un solo usuario eso significa que el período no se puede cerrar nunca, y es un muro que aparece al final, cuando ya se cargó todo.")}</>
    ),
  },

  facturacion: {
    titulo: "Lo que facturaste",
    marty: "Dame tu versión de los números y la contrasto contra lo que encuentre en los comprobantes.",
    que: "La hoja de cálculo con la que facturas hoy, como esté.",
    como: "Con las columnas que ya tenga y en el orden que ya tenga. No la toques. Y si prefieres, factura por factura aquí mismo.",
    formulario: "facturacion",
    siSePospone:
      "No hay ingreso por cliente, ni concentración, ni DSO: el libro no guarda a quién se le vendió.",
    porque: (
      <>
        {t("La hoja es lo que")}{" "}<em>{t("tú crees")}</em> {" "}{t("que facturaste; los comprobantes son lo que")}{" "}
        <em>facturaste</em>{t(". Tener las dos y compararlas es todo el punto — la diferencia entre ambas casi siempre es un hallazgo real, no un error de carga.")}</>
    ),
  },

  apertura: {
    titulo: "Los saldos de apertura",
    marty: "Engancho tu histórico con lo que ya tenías antes. Si no cuadra, te lo digo antes de seguir.",
    que: (
      <>
        {t("El balance de cierre de tu sistema anterior. Importan los que")}{" "}<em>arrastran</em>{t(": crédito fiscal, saldo a favor de renta, pérdidas arrastrables, cuentas por cobrar y por pagar.")}</>
    ),
    como: "Como te lo entregue tu contador anterior. Si viene de CONCAR, CONTPAQi u Odoo, lo leo tal cual y lo cargas aquí mismo.",
    formulario: "historico",
    accion: { etiqueta: "Ir al diario", a: "/contabilidad/libros/diario" },
    siSePospone:
      "Los estados no enganchan con lo que había antes. Si arrancas de cero, este paso no aplica y posponerlo es lo correcto.",
    porque: (
      <>
        <strong>{t("Tiene que cuadrar, y esto no se negocia.")}</strong> {" "}{t("Si el asiento de apertura no cuadra, el problema está en la información que entregó el sistema anterior y hay que resolverlo antes de seguir: arrancar con un descuadre garantiza que todos los cierres siguientes lo arrastren. Si arrancas de cero, este paso no aplica.")}</>
    ),
  },

  cierre: {
    titulo: "Revisar y cuadrar",
    marty: "Propongo una cuenta para cada comprobante: corrígeme y no te lo vuelvo a preguntar. Y compruebo que no falte nada contra un número que tú ya te creías.",
    que: "Aprobar o corregir lo que propuse, de a uno, en la cola. Y los totales que tu sistema anterior declaraba por año: un número por año basta.",
    como: "La cola, en Contabilidad › Revisión. El cuadre, en Finanzas › Cuadres: un reporte, una captura o el número escrito.",
    acciones: [
      { etiqueta: "Ir a la cola", a: "/contabilidad/revision" },
      { etiqueta: "Ir a los cuadres", a: "/finanzas/cuadres" },
    ],
    siSePospone:
      "Los comprobantes quedan cargados y sin asentar, y el traspaso queda sin cerrar: nadie comprobó que no falte nada.",
    porque: (
      <>
        <strong>{t("El primer mes la cola es grande y eso es lo esperado, no una falla.")}</strong> {" "}{t("Mi memoria de cada proveedor arranca vacía; a partir de la tercera confirmación clasifico solo. Y el cuadre es el único cierre del traspaso que vale:")}{" "}
        <em>{t("&laquo;Importé 890 documentos&raquo;")}</em> {" "}{t("no lo puede validar nadie;")}{" "}
        <em>
          {t("&laquo;tu sistema decía 2.431.000 y cargué 2.418.500; la diferencia son 7 notas de crédito que no estaban en la carpeta&raquo;")}</em>{" "}
        {t("se valida en diez segundos.")}</>
    ),
  },
}

export const ETIQUETA_ESTADO: Record<EstadoPaso, string> = {
  pendiente: "Sin empezar",
  en_curso: "En curso",
  listo: "Listo",
  atencion: "Necesita atención",
}

/** El orden de la pantalla lo fija el backend; esto solo rellena huecos. */
export function textoDe(paso: PasoTraspaso): TextoPaso {
  return (
    TEXTOS[paso.clave] ?? {
      titulo: paso.clave,
      marty: "",
      que: "—",
    }
  )
}
